/**
 * CDM Remediation — Track 6A "soft" remediation surface.
 *
 * Three tabs:
 *  - Auto-tickets:  cdm_compliance_tickets (open/close ServiceNow / Jira / webhook)
 *  - Tag-backs:     cdm_writeback_outbox rows where field ends in _tag/_annotation/_metadata/_extension
 *  - CMDB queue:    cdm_writeback_outbox rows for the BMC Helix dry-run pipeline
 *
 * No production-changing actions live on this page — every push is
 * either a ticket (ServiceNow / Jira / webhook) or a queue row that
 * an operator reviews. The four-phase rollout (off / dry_run / propose
 * / auto) is surfaced via the cdm_writeback_capability matrix.
 */
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Wrench, ChevronLeft, RefreshCw, Play, AlertTriangle, CheckCircle2,
  XCircle, Clock, Database, Tag, Ticket, ExternalLink,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface ComplianceTicket {
  id: string
  finding_rule_id: string
  asset_id: string | null
  target: string
  external_ticket_id: string | null
  state: string
  opened_at: string | null
  closed_at: string | null
  last_synced_at: string | null
  summary: string | null
  severity: string | null
}

interface OutboxRow {
  id: string
  golden_id: string
  archetype: string
  field: string
  source: string
  source_id: string
  status: string
  canonical_value: any
  preflight_value: any
  source_value_before: any
  source_value_after: any
  intended_at: string | null
  pushed_at: string | null
  verified_at: string | null
  approver_id: string | null
  retry_count: number
  last_error: string | null
  is_dry_run: boolean
}

interface Capability {
  source: string
  field: string
  pushable: boolean
  mode: string
  api_method: string | null
  requires_approval: boolean
  approver_role: string | null
  rate_limit_per_min: number | null
  notes: string | null
}

const TICKET_STATE_TONE: Record<string, string> = {
  open:           'bg-amber/15 text-amber-light border-amber/30',
  closed:         'bg-emerald/15 text-emerald border-emerald/30',
  failed_to_open: 'bg-rose/15 text-rose-light border-rose/30',
}

const OUTBOX_STATUS_TONE: Record<string, string> = {
  queued:             'bg-text-muted/15 text-text-muted border-border',
  preflight_ok:       'bg-text-muted/15 text-text-muted border-border',
  preflight_failed:   'bg-rose/15 text-rose-light border-rose/30',
  awaiting_approval:  'bg-amber/15 text-amber-light border-amber/30',
  approved:           'bg-blue/15 text-blue-light border-blue/30',
  executing:          'bg-blue/15 text-blue-light border-blue/30',
  executed:           'bg-emerald/15 text-emerald border-emerald/30',
  verified:           'bg-emerald/15 text-emerald border-emerald/30',
  failed_silent:      'bg-rose/15 text-rose-light border-rose/30',
  failed_error:       'bg-rose/15 text-rose-light border-rose/30',
  rolled_back:        'bg-rose/15 text-rose-light border-rose/30',
}

const SEV_TONE: Record<string, string> = {
  critical: 'text-rose-light',
  high:     'text-rose-light',
  medium:   'text-amber-light',
  low:      'text-text-muted',
}

const TAB_FIELDS = ['_tag', '_annotation', '_metadata', '_extension']

type Tab = 'tickets' | 'tagbacks' | 'cmdb'

export default function CdmRemediationPage() {
  const [tab, setTab] = useState<Tab>('tickets')
  const [tickets, setTickets] = useState<ComplianceTicket[]>([])
  const [outbox, setOutbox] = useState<OutboxRow[]>([])
  const [capabilities, setCapabilities] = useState<Capability[]>([])
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const [message, setMessage] = useState('')
  const [stateFilter, setStateFilter] = useState('')
  const [targetFilter, setTargetFilter] = useState('')

  const isTagBack = (field: string) => TAB_FIELDS.some(suf => field.endsWith(suf))
  const isCmdb = (source: string) => source === 'bmc_helix'

  const tagBacks = useMemo(
    () => outbox.filter(r => isTagBack(r.field) && !isCmdb(r.source)),
    [outbox],
  )
  const cmdbRows = useMemo(
    () => outbox.filter(r => isCmdb(r.source)),
    [outbox],
  )

  const load = () => {
    setLoading(true)
    const tQuery = new URLSearchParams()
    if (stateFilter) tQuery.set('state', stateFilter)
    if (targetFilter) tQuery.set('target', targetFilter)
    Promise.all([
      api.get('/api/cdm/auto-ticket/tickets?' + tQuery.toString()),
      api.get('/api/cdm/writeback/queue?limit=300'),
      api.get('/api/cdm/writeback/capabilities'),
    ])
      .then(([t, w, c]) => {
        setTickets(t.data?.tickets || [])
        setOutbox(w.data?.rows || [])
        setCapabilities(c.data?.capabilities || [])
      })
      .catch(e => setMessage(`Load failed: ${e.response?.data?.detail || e.message}`))
      .finally(() => setLoading(false))
  }
  useEffect(() => { load() }, [stateFilter, targetFilter]) // eslint-disable-line react-hooks/exhaustive-deps

  const reconcile = async () => {
    setBusy('reconcile')
    setMessage('Reconciling tickets…')
    try {
      const { data } = await api.post('/api/cdm/auto-ticket/run', null,
                                       { params: { target: 'auto', max_per_run: 50 } })
      setMessage(`Reconciled: ${data.opened ?? 0} opened, ${data.closed ?? 0} closed, ${data.skipped ?? 0} skipped, ${data.failed ?? 0} failed (${data.total_findings_seen ?? 0} findings seen)`)
      load()
    } catch (e: any) {
      setMessage(`Reconcile failed: ${e.response?.data?.detail || e.message}`)
    } finally {
      setBusy(null)
    }
  }

  const dryRunScan = async () => {
    setBusy('dryrun')
    setMessage('Scanning recent conflicts for BMC Helix dry-run…')
    try {
      const { data } = await api.post('/api/cdm/writeback/helix/dry-run', null,
                                       { params: { limit: 50 } })
      setMessage(`Dry-run: ${data.queued ?? 0} queued, ${data.skipped ?? 0} skipped, ${data.scanned ?? 0} scanned (${data.phase})`)
      load()
    } catch (e: any) {
      setMessage(`Dry-run failed: ${e.response?.data?.detail || e.message}`)
    } finally {
      setBusy(null)
    }
  }

  const counts = {
    tickets:  tickets.length,
    tagbacks: tagBacks.length,
    cmdb:     cmdbRows.length,
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-baseline gap-4 pb-2 border-b border-border flex-wrap">
        <Link to="/app/cdm" className="text-text-muted hover:text-text-primary inline-flex items-center gap-1">
          <ChevronLeft size={14} /> CDM
        </Link>
        <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
          <Wrench size={18} className="text-cyan" /> CDM REMEDIATION
        </h1>
        <span className="text-[11px] uppercase tracking-widest text-text-muted">
          soft remediation · tickets · tag-backs · CMDB write-back queue
        </span>
        <div className="ml-auto flex items-center gap-2">
          <button type="button" onClick={load} disabled={loading} className="btn-ghost">
            <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {message && (
        <div className="text-[11px] text-text-secondary font-mono px-3 py-2 border border-border rounded bg-surface-card">
          {message}
        </div>
      )}

      {/* Tab strip */}
      <div className="flex items-center gap-1 border-b border-border">
        <TabButton active={tab === 'tickets'} onClick={() => setTab('tickets')}
                   icon={Ticket} label={`Auto-tickets · ${counts.tickets}`} />
        <TabButton active={tab === 'tagbacks'} onClick={() => setTab('tagbacks')}
                   icon={Tag} label={`Tag-backs · ${counts.tagbacks}`} />
        <TabButton active={tab === 'cmdb'} onClick={() => setTab('cmdb')}
                   icon={Database} label={`CMDB queue · ${counts.cmdb}`} />
      </div>

      {/* Auto-tickets */}
      {tab === 'tickets' && (
        <div className="space-y-3">
          <div className="card p-3 space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[11px] uppercase tracking-widest text-text-muted">
                Filters
              </span>
              <select value={stateFilter} onChange={e => setStateFilter(e.target.value)}
                      className="bg-surface-raised text-xs border border-border rounded px-2 py-1">
                <option value="">All states</option>
                <option value="open">Open</option>
                <option value="closed">Closed</option>
                <option value="failed_to_open">Failed to open</option>
              </select>
              <select value={targetFilter} onChange={e => setTargetFilter(e.target.value)}
                      className="bg-surface-raised text-xs border border-border rounded px-2 py-1">
                <option value="">All targets</option>
                <option value="servicenow">ServiceNow</option>
                <option value="jira">Jira</option>
                <option value="webhook">Webhook</option>
              </select>
              <div className="ml-auto flex items-center gap-2">
                <button type="button" onClick={reconcile} disabled={!!busy} className="btn-primary text-[11px]">
                  <Play size={11} /> Reconcile now
                </button>
              </div>
            </div>
            <p className="text-[10px] text-text-muted">
              Reconcile walks current findings, opens a ticket per new fail,
              closes tickets whose finding now passes. Idempotent — safe to
              run on cron and on-demand. Cron runs every 6h at :50.
            </p>
          </div>

          <div className="card p-0 overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-surface-raised">
                <tr className="text-[10px] uppercase tracking-widest text-text-muted border-b border-border">
                  <th className="text-left px-3 py-2">State</th>
                  <th className="text-left px-3 py-2">Rule</th>
                  <th className="text-left px-3 py-2">Asset</th>
                  <th className="text-left px-3 py-2">Severity</th>
                  <th className="text-left px-3 py-2">Target</th>
                  <th className="text-left px-3 py-2">External ID</th>
                  <th className="text-left px-3 py-2">Opened</th>
                  <th className="text-left px-3 py-2">Closed</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {tickets.length === 0 && !loading && (
                  <tr><td colSpan={8} className="px-3 py-6 text-center text-text-muted">
                    No tickets yet. Click 'Reconcile now' to evaluate findings
                    and open tickets for new fails.
                  </td></tr>
                )}
                {tickets.map(t => (
                  <tr key={t.id} className="hover:bg-surface-raised/40">
                    <td className="px-3 py-1.5">
                      <span className={clsx('px-2 py-0.5 rounded border text-[10px] uppercase tracking-wider',
                                            TICKET_STATE_TONE[t.state] || 'bg-text-muted/15 text-text-muted border-border')}>
                        {t.state}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 font-mono text-[11px]">{t.finding_rule_id}</td>
                    <td className="px-3 py-1.5 font-mono text-[10px] text-text-secondary">
                      {t.asset_id ? t.asset_id.slice(0, 8) + '…' : '(org)'}
                    </td>
                    <td className={clsx('px-3 py-1.5 text-[11px]', t.severity ? SEV_TONE[t.severity] : 'text-text-muted')}>
                      {t.severity || '—'}
                    </td>
                    <td className="px-3 py-1.5 text-text-secondary">{t.target}</td>
                    <td className="px-3 py-1.5 font-mono text-[10px] text-text-secondary">
                      {t.external_ticket_id || '—'}
                    </td>
                    <td className="px-3 py-1.5 text-[10px] text-text-muted">
                      {t.opened_at ? new Date(t.opened_at).toLocaleString() : '—'}
                    </td>
                    <td className="px-3 py-1.5 text-[10px] text-text-muted">
                      {t.closed_at ? new Date(t.closed_at).toLocaleString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tag-backs */}
      {tab === 'tagbacks' && (
        <div className="space-y-3">
          <div className="card p-3 text-[11px] text-text-muted">
            Tag-backs push approved CDM tags (e.g. <code>owning_team</code>)
            into the originating source as a tag / annotation / custom-property.
            Failures stay in the queue with status <code>failed_error</code>
            so you can see the gap rather than have it silently dropped.
          </div>

          <div className="card p-0 overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-surface-raised">
                <tr className="text-[10px] uppercase tracking-widest text-text-muted border-b border-border">
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">Source</th>
                  <th className="text-left px-3 py-2">Field</th>
                  <th className="text-left px-3 py-2">Source ID</th>
                  <th className="text-left px-3 py-2">Value</th>
                  <th className="text-left px-3 py-2">Approver</th>
                  <th className="text-left px-3 py-2">When</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {tagBacks.length === 0 && !loading && (
                  <tr><td colSpan={7} className="px-3 py-6 text-center text-text-muted">
                    No tag-back attempts yet. Approve an orphan-owner proposal
                    in <Link to="/app/cdm/policy-proposals" className="text-blue-light hover:underline">
                    CDM Policy Proposals</Link> to fire a tag-back.
                  </td></tr>
                )}
                {tagBacks.map(r => (
                  <tr key={r.id} className="hover:bg-surface-raised/40">
                    <td className="px-3 py-1.5">
                      <span className={clsx('px-2 py-0.5 rounded border text-[10px] uppercase tracking-wider',
                                            OUTBOX_STATUS_TONE[r.status] || 'bg-text-muted/15 text-text-muted border-border')}>
                        {r.status}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-text-secondary">{r.source}</td>
                    <td className="px-3 py-1.5 font-mono text-[10px] text-text-secondary">{r.field}</td>
                    <td className="px-3 py-1.5 font-mono text-[10px] text-text-secondary truncate max-w-[200px]">
                      {r.source_id || '—'}
                    </td>
                    <td className="px-3 py-1.5 font-mono text-[10px] text-text-secondary">
                      {r.canonical_value ? (
                        <span title={JSON.stringify(r.canonical_value)}>
                          {String(r.canonical_value.value ?? JSON.stringify(r.canonical_value)).slice(0, 60)}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-3 py-1.5 text-[10px] text-text-muted">{r.approver_id || '—'}</td>
                    <td className="px-3 py-1.5 text-[10px] text-text-muted">
                      {r.pushed_at ? new Date(r.pushed_at).toLocaleString()
                        : r.intended_at ? new Date(r.intended_at).toLocaleString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Capabilities matrix — only the tag-back-relevant rows */}
          <CapabilitiesPanel
            capabilities={capabilities.filter(c => TAB_FIELDS.some(s => c.field.endsWith(s)))}
            title="Tag-back capabilities (mode rollout)"
          />
        </div>
      )}

      {/* CMDB queue */}
      {tab === 'cmdb' && (
        <div className="space-y-3">
          <div className="card p-3 space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <AlertTriangle size={14} className="text-amber-light" />
              <span className="text-[11px] uppercase tracking-widest text-text-muted">
                BMC Helix · PHASE A · dry-run only
              </span>
              <div className="ml-auto">
                <button type="button" onClick={dryRunScan} disabled={!!busy}
                        className="btn-primary text-[11px]">
                  <Play size={11} /> Run dry-run scan
                </button>
              </div>
            </div>
            <p className="text-[10px] text-text-muted">
              Walks recent conflicts where BMC Helix was on the wrong side
              and queues a dry-run row showing what we WOULD push. Real
              CMDB mutations stay disabled until an admin flips the
              capability mode to <code>auto</code>.
            </p>
          </div>

          <div className="card p-0 overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-surface-raised">
                <tr className="text-[10px] uppercase tracking-widest text-text-muted border-b border-border">
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">Field</th>
                  <th className="text-left px-3 py-2">Asset</th>
                  <th className="text-left px-3 py-2">CI ID</th>
                  <th className="text-left px-3 py-2">Before → After</th>
                  <th className="text-left px-3 py-2">When</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {cmdbRows.length === 0 && !loading && (
                  <tr><td colSpan={6} className="px-3 py-6 text-center text-text-muted">
                    Queue empty. Click 'Run dry-run scan' to populate the
                    queue from recent conflicts.
                  </td></tr>
                )}
                {cmdbRows.map(r => (
                  <tr key={r.id} className="hover:bg-surface-raised/40">
                    <td className="px-3 py-1.5">
                      <span className={clsx('px-2 py-0.5 rounded border text-[10px] uppercase tracking-wider',
                                            OUTBOX_STATUS_TONE[r.status] || 'bg-text-muted/15 text-text-muted border-border')}>
                        {r.status}
                      </span>
                      {r.is_dry_run && (
                        <span className="ml-1 px-1.5 py-0.5 rounded border border-amber/30 bg-amber/15 text-amber-light text-[9px] uppercase tracking-wider">
                          dry-run
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-1.5 font-mono text-[10px] text-text-secondary">{r.field}</td>
                    <td className="px-3 py-1.5 font-mono text-[10px] text-text-secondary">
                      <Link to={`/app/cdm/investigation/${r.golden_id}`}
                            className="text-blue-light hover:underline">
                        {r.golden_id.slice(0, 8)}…
                      </Link>
                    </td>
                    <td className="px-3 py-1.5 font-mono text-[10px] text-text-secondary">
                      {r.source_id || '—'}
                    </td>
                    <td className="px-3 py-1.5 font-mono text-[10px]">
                      <DiffCell before={r.source_value_before} after={r.source_value_after} />
                    </td>
                    <td className="px-3 py-1.5 text-[10px] text-text-muted">
                      {r.pushed_at ? new Date(r.pushed_at).toLocaleString()
                        : r.intended_at ? new Date(r.intended_at).toLocaleString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <CapabilitiesPanel
            capabilities={capabilities.filter(c => c.source === 'bmc_helix')}
            title="CMDB write-back capabilities (mode rollout)"
          />
        </div>
      )}
    </div>
  )
}


function TabButton({ active, onClick, icon: Icon, label }: {
  active: boolean
  onClick: () => void
  icon: React.ElementType
  label: string
}) {
  return (
    <button type="button" onClick={onClick}
            className={clsx(
              'inline-flex items-center gap-1.5 px-3 py-2 text-[11px] uppercase tracking-widest border-b-2 transition-colors',
              active ? 'border-cyan text-text-primary' : 'border-transparent text-text-muted hover:text-text-secondary',
            )}>
      <Icon size={12} /> {label}
    </button>
  )
}


function DiffCell({ before, after }: { before: any, after: any }) {
  const beforeS = before == null ? '—' : (before.value ?? JSON.stringify(before))
  const afterS  = after  == null ? '—' : (after.value  ?? JSON.stringify(after))
  return (
    <div className="flex items-center gap-1">
      <span className="text-text-muted line-through">{String(beforeS).slice(0, 30)}</span>
      <span className="text-text-muted">→</span>
      <span className="text-emerald">{String(afterS).slice(0, 30)}</span>
    </div>
  )
}


function CapabilitiesPanel({ capabilities, title }: {
  capabilities: Capability[]
  title: string
}) {
  if (capabilities.length === 0) return null
  return (
    <div className="card p-0 overflow-hidden">
      <div className="px-3 py-2 border-b border-border">
        <h3 className="text-[11px] uppercase tracking-widest text-text-muted">{title}</h3>
      </div>
      <table className="w-full text-xs">
        <thead className="bg-surface-raised">
          <tr className="text-[10px] uppercase tracking-widest text-text-muted border-b border-border">
            <th className="text-left px-3 py-2">Source</th>
            <th className="text-left px-3 py-2">Field</th>
            <th className="text-left px-3 py-2">Mode</th>
            <th className="text-left px-3 py-2">Pushable</th>
            <th className="text-left px-3 py-2">Approval</th>
            <th className="text-left px-3 py-2">Notes</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {capabilities.map(c => (
            <tr key={`${c.source}:${c.field}`}>
              <td className="px-3 py-1.5 text-text-secondary">{c.source}</td>
              <td className="px-3 py-1.5 font-mono text-[10px] text-text-secondary">{c.field}</td>
              <td className="px-3 py-1.5">
                <span className={clsx('px-2 py-0.5 rounded border text-[10px] uppercase tracking-wider',
                  c.mode === 'auto' ? 'bg-emerald/15 text-emerald border-emerald/30'
                  : c.mode === 'propose' ? 'bg-blue/15 text-blue-light border-blue/30'
                  : c.mode === 'dry_run' ? 'bg-amber/15 text-amber-light border-amber/30'
                  : 'bg-text-muted/15 text-text-muted border-border'
                )}>
                  {c.mode}
                </span>
              </td>
              <td className="px-3 py-1.5">
                {c.pushable
                  ? <CheckCircle2 size={12} className="text-emerald" />
                  : <XCircle size={12} className="text-text-muted" />}
              </td>
              <td className="px-3 py-1.5 text-[10px] text-text-secondary">
                {c.requires_approval ? `yes${c.approver_role ? ` · ${c.approver_role}` : ''}` : 'no'}
              </td>
              <td className="px-3 py-1.5 text-[10px] text-text-muted truncate max-w-[280px]">
                {c.notes || '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
